import { z } from 'zod';

// -----------------------------------------------------------------------------
// Enums
// -----------------------------------------------------------------------------

export const CrmAccountStatusSchema = z.enum(['active', 'inactive']);
export type CrmAccountStatus = z.infer<typeof CrmAccountStatusSchema>;

export const ContactStatusSchema = z.enum(['active', 'inactive']);
export type ContactStatus = z.infer<typeof ContactStatusSchema>;

export const LeadStatusSchema = z.enum(['new', 'contacted', 'qualified', 'converted', 'lost']);
export type LeadStatus = z.infer<typeof LeadStatusSchema>;

export const OpportunityStatusSchema = z.enum(['open', 'won', 'lost']);
export type OpportunityStatus = z.infer<typeof OpportunityStatusSchema>;

export const ActivityKindSchema = z.enum(['call', 'email', 'meeting', 'note', 'task']);
export type ActivityKind = z.infer<typeof ActivityKindSchema>;

export const ActivityRelatedTypeSchema = z.enum(['lead', 'contact', 'account', 'opportunity']);
export type ActivityRelatedType = z.infer<typeof ActivityRelatedTypeSchema>;

export const EmployeeCountRangeSchema = z.enum(['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5001+']);
export type EmployeeCountRange = z.infer<typeof EmployeeCountRangeSchema>;

const decimalString = z.string().regex(/^-?\d+(\.\d{1,4})?$/, 'expected decimal');
const positiveDecimalString = z.string().regex(/^\d+(\.\d{1,4})?$/, 'expected non-negative decimal');
const probabilityString = z.string().regex(/^(100(\.00)?|\d{1,2}(\.\d{1,2})?)$/, 'expected 0-100 probability');
const currencyCode = z.string().length(3).regex(/^[A-Z]{3}$/);
const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// -----------------------------------------------------------------------------
// Accounts
// -----------------------------------------------------------------------------

export const CrmAccountCreateSchema = z.object({
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  industry: z.string().max(100).nullable().optional(),
  website: z.string().max(200).nullable().optional(),
  phone: z.string().max(32).nullable().optional(),
  annualRevenue: positiveDecimalString.nullable().optional(),
  revenueCurrencyCode: currencyCode.nullable().optional(),
  employeeCountRange: EmployeeCountRangeSchema.nullable().optional(),
  address: z.record(z.unknown()).nullable().optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
  notes: z.string().max(5_000).nullable().optional(),
});
export type CrmAccountCreate = z.infer<typeof CrmAccountCreateSchema>;

export const CrmAccountUpdateSchema = CrmAccountCreateSchema.partial().extend({
  status: CrmAccountStatusSchema.optional(),
});
export type CrmAccountUpdate = z.infer<typeof CrmAccountUpdateSchema>;

export const CrmAccountSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  industry: z.string().nullable(),
  website: z.string().nullable(),
  phone: z.string().nullable(),
  annualRevenue: z.string().nullable(),
  revenueCurrencyCode: z.string().nullable(),
  employeeCountRange: EmployeeCountRangeSchema.nullable(),
  address: z.record(z.unknown()).nullable(),
  partyId: z.string().uuid().nullable(),
  ownerUserId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  status: CrmAccountStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CrmAccount = z.infer<typeof CrmAccountSchema>;

// -----------------------------------------------------------------------------
// Contacts
// -----------------------------------------------------------------------------

export const ContactCreateSchema = z.object({
  accountId: z.string().uuid().nullable().optional(),
  firstName: z.string().min(1).max(120),
  lastName: z.string().min(1).max(120),
  displayName: z.string().min(1).max(240).optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(32).nullable().optional(),
  title: z.string().max(120).nullable().optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
  notes: z.string().max(5_000).nullable().optional(),
});
export type ContactCreate = z.infer<typeof ContactCreateSchema>;

export const ContactUpdateSchema = ContactCreateSchema.partial().extend({
  status: ContactStatusSchema.optional(),
});
export type ContactUpdate = z.infer<typeof ContactUpdateSchema>;

export const ContactSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  accountId: z.string().uuid().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  title: z.string().nullable(),
  ownerUserId: z.string().uuid().nullable(),
  partyId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  status: ContactStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Contact = z.infer<typeof ContactSchema>;

// -----------------------------------------------------------------------------
// Pipelines + Stages
// -----------------------------------------------------------------------------

export const PipelineCreateSchema = z.object({
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
});
export type PipelineCreate = z.infer<typeof PipelineCreateSchema>;

export const PipelineSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Pipeline = z.infer<typeof PipelineSchema>;

export const StageCreateSchema = z.object({
  pipelineId: z.string().uuid(),
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  sortOrder: z.number().int().min(0).max(1000),
  probability: probabilityString.optional(),
  isWon: z.boolean().optional(),
  isLost: z.boolean().optional(),
});
export type StageCreate = z.infer<typeof StageCreateSchema>;

export const StageSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  pipelineId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
  probability: z.string(),
  isWon: z.boolean(),
  isLost: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Stage = z.infer<typeof StageSchema>;

/**
 * Pipeline with its stages joined in. The list endpoint returns these
 * so the UI can populate a cascading pipeline → stage picker (Move-
 * stage flow on /crm/opportunities) without a second round-trip.
 * Stages are ordered by sortOrder ascending.
 */
export const PipelineWithStagesSchema = PipelineSchema.extend({
  stages: z.array(StageSchema),
});
export type PipelineWithStages = z.infer<typeof PipelineWithStagesSchema>;

// -----------------------------------------------------------------------------
// Leads
// -----------------------------------------------------------------------------

export const LeadCreateSchema = z.object({
  firstName: z.string().min(1).max(120),
  lastName: z.string().min(1).max(120),
  displayName: z.string().min(1).max(240).optional(),
  company: z.string().max(200).nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(32).nullable().optional(),
  source: z.string().max(64).nullable().optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
  notes: z.string().max(5_000).nullable().optional(),
});
export type LeadCreate = z.infer<typeof LeadCreateSchema>;

export const LeadUpdateSchema = LeadCreateSchema.partial().extend({
  status: LeadStatusSchema.optional(),
});
export type LeadUpdate = z.infer<typeof LeadUpdateSchema>;

export const LeadSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  company: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  source: z.string().nullable(),
  status: LeadStatusSchema,
  ownerUserId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  convertedAt: z.string().nullable(),
  convertedToAccountId: z.string().uuid().nullable(),
  convertedToContactId: z.string().uuid().nullable(),
  convertedToOpportunityId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Lead = z.infer<typeof LeadSchema>;

export const LeadConvertSchema = z.object({
  accountId: z.string().uuid().optional(),
  accountName: z.string().min(1).max(200).optional(),
  pipelineId: z.string().uuid().optional(),
  stageId: z.string().uuid().optional(),
  opportunityName: z.string().min(1).max(200),
  amount: positiveDecimalString.optional(),
  currencyCode: currencyCode.optional(),
  expectedCloseDate: dateString.optional(),
});
export type LeadConvert = z.infer<typeof LeadConvertSchema>;

// -----------------------------------------------------------------------------
// Opportunities
// -----------------------------------------------------------------------------

export const OpportunityCreateSchema = z.object({
  code: z.string().min(1).max(64).optional(),    // auto-generated when absent
  name: z.string().min(1).max(200),
  accountId: z.string().uuid(),
  primaryContactId: z.string().uuid().nullable().optional(),
  pipelineId: z.string().uuid().optional(),       // defaults to tenant default
  stageId: z.string().uuid().optional(),
  amount: positiveDecimalString.optional(),
  currencyCode: currencyCode.optional(),
  expectedCloseDate: dateString.nullable().optional(),
  probability: probabilityString.optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
  notes: z.string().max(5_000).nullable().optional(),
});
export type OpportunityCreate = z.infer<typeof OpportunityCreateSchema>;

export const OpportunityUpdateSchema = OpportunityCreateSchema.partial();
export type OpportunityUpdate = z.infer<typeof OpportunityUpdateSchema>;

export const OpportunitySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  accountId: z.string().uuid(),
  primaryContactId: z.string().uuid().nullable(),
  pipelineId: z.string().uuid(),
  stageId: z.string().uuid(),
  amount: z.string(),
  currencyCode: z.string(),
  expectedCloseDate: z.string().nullable(),
  probability: z.string(),
  status: OpportunityStatusSchema,
  closedAt: z.string().nullable(),
  closeReason: z.string().nullable(),
  /**
   * The quotation this deal became, once somebody has quoted it. A SOFT
   * reference: the office may delete a quotation long after the deal is filed,
   * so a missing one reads as null rather than breaking the opportunity.
   */
  quotationId: z.string().uuid().nullable(),
  /** That quotation's human number (QUO-000042), resolved for display. */
  quotationNumber: z.string().nullable(),
  ownerUserId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Opportunity = z.infer<typeof OpportunitySchema>;

export const StageMoveSchema = z.object({
  stageId: z.string().uuid(),
});
export type StageMove = z.infer<typeof StageMoveSchema>;

export const OpportunityCloseSchema = z.object({
  reason: z.string().max(200).optional(),
});
export type OpportunityClose = z.infer<typeof OpportunityCloseSchema>;

// -----------------------------------------------------------------------------
// Activities
// -----------------------------------------------------------------------------

export const ActivityCreateSchema = z.object({
  kind: ActivityKindSchema,
  subject: z.string().min(1).max(200),
  body: z.string().max(20_000).nullable().optional(),
  relatedType: ActivityRelatedTypeSchema,
  relatedId: z.string().uuid(),
  dueAt: z.string().datetime().nullable().optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
});
export type ActivityCreate = z.infer<typeof ActivityCreateSchema>;

export const ActivityUpdateSchema = ActivityCreateSchema.partial().extend({
  completedAt: z.string().datetime().nullable().optional(),
});
export type ActivityUpdate = z.infer<typeof ActivityUpdateSchema>;

export const ActivitySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  kind: ActivityKindSchema,
  subject: z.string(),
  body: z.string().nullable(),
  relatedType: ActivityRelatedTypeSchema,
  relatedId: z.string().uuid(),
  dueAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  ownerUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Activity = z.infer<typeof ActivitySchema>;

// Silence unused-var lint for the helper schemas (kept for future use).
void decimalString;
